import { useCallback, useRef, useState } from 'react';

/**
 * Tracks a multi-row table selection *and* keeps the selected row objects in
 * memory — even for rows that have since scrolled off / paged away. `DataTable`
 * only reports selected ids; bulk actions need the rows themselves (a variant's
 * product id, inventory item, price…), so this remembers every row as it is
 * checked and forgets it when unchecked.
 */
export function useRowSelection<T>(getId: (row: T) => string) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const rows = useRef<Map<string, T>>(new Map());

  /**
   * Apply a new id set from `DataTable`. `visible` is the data currently
   * rendered — the only place newly-checked rows can be looked up.
   */
  const setSelection = useCallback(
    (next: Set<string>, visible: T[]) => {
      const visibleById = new Map(visible.map((r) => [getId(r), r]));
      const nextRows = new Map<string, T>();
      next.forEach((id) => {
        const row = rows.current.get(id) ?? visibleById.get(id);
        if (row) nextRows.set(id, row);
      });
      rows.current = nextRows;
      setSelectedIds(next);
    },
    [getId],
  );

  const clear = useCallback(() => {
    rows.current = new Map();
    setSelectedIds(new Set());
  }, []);

  /** The selected row objects, in no particular order. */
  const getSelectedRows = useCallback(() => [...rows.current.values()], []);

  return {
    selectedIds,
    count: selectedIds.size,
    setSelection,
    clear,
    getSelectedRows,
  };
}
